import React from 'react';

/**
 * Shared ConnectMail brand logo.
 *
 * Variants:
 *   - "icon"       → orb-only square icon (default for compact spots)
 *   - "full"       → vertical lockup image (icon + wordmark + tagline) from /brand/logo-full.png
 *   - "horizontal" → icon + "ConnectMail" wordmark rendered side-by-side. Best for headers / auth pages.
 *
 * Props:
 *   - size (number)      : px size for icon variant
 *   - height (string)    : Tailwind height class for full/horizontal variants (e.g. 'h-10')
 *   - tone ('dark'|'light') : controls wordmark text color on horizontal variant.
 *                              'dark' → on dark/navy backgrounds (white text)
 *                              'light' → on light backgrounds (gradient brand text)
 *   - showTagline (bool) : show "Connect • Promote • Earn • Grow" tagline on horizontal variant
 */
export const Logo = ({
  variant = 'icon',
  size = 36,
  height = 'h-10',
  tone = 'dark',
  showTagline = false,
  className = '',
  ...rest
}) => {
  if (variant === 'full') {
    return (
      <img
        src="/brand/logo-full.png"
        alt="ConnectMail"
        className={`${height} w-auto object-contain ${className}`}
        {...rest}
      />
    );
  }

  if (variant === 'horizontal') {
    const wordmarkClass =
      tone === 'dark'
        ? 'text-white'
        : 'bg-gradient-to-r from-[#1e3a5f] via-[#1f7ab3] to-[#22c55e] bg-clip-text text-transparent';
    const taglineClass = tone === 'dark' ? 'text-white/70' : 'text-emerald-700/80';
    return (
      <div className={`flex items-center gap-2.5 ${className}`} {...rest}>
        <img
          src="/brand/logo-icon.png"
          alt="ConnectMail logo"
          className={`${height} w-auto object-contain shrink-0`}
          data-testid="logo-icon-img"
        />
        <div className="flex flex-col leading-none">
          <span
            className={`font-extrabold tracking-tight font-['Outfit'] text-2xl sm:text-3xl ${wordmarkClass}`}
            data-testid="logo-wordmark"
          >
            ConnectMail
          </span>
          {showTagline && (
            <span
              className={`mt-1 text-[10px] sm:text-[11px] font-semibold tracking-[0.18em] uppercase ${taglineClass}`}
              data-testid="logo-tagline"
            >
              Connect · Promote · Earn · Grow
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <img
      src="/brand/logo-icon.png"
      alt="ConnectMail"
      width={size}
      height={size}
      style={{ width: size, height: size, objectFit: 'contain' }}
      className={className}
      {...rest}
    />
  );
};

export default Logo;
